import { SearchRegistry } from '../types';

export const customerSupportSearchEntries: SearchRegistry = {
  '/ryd-customer-support/overview': {
    keywords: [
      'customer support',
      'support',
      'overview',
      'search',
      'overview & search',
      'support dashboard',
      'support home',
      'customer service',
      'helpdesk',
      'cs overview',
      'kundensupport',
      'kundenservice',
      'search workbench',
      'search categories',
      'search types',
      'search chips',
      'chip group',
      'search panel',
      'pick a category',
      'search help',
      'name-partial',
    ],
    questions: [
      'Where is the customer support landing page?',
      'How do I start a customer support investigation?',
      'Which lookup should I use for a given customer identifier?',
      'How does the Search Workbench work?',
    ],
    faqs: [
      {
        q: 'Where do I start when a customer reports an issue?',
        a: 'Open Customer Support → Overview & Search. The Search Workbench groups every lookup (order UUID, voucher code, email, name, payment email, invoice, Salesforce invoices) into one chip-based picker; pick a category, paste the value and jump straight into the matching workflow.',
      },
      {
        q: 'How do I get a quick overview of the available customer-support search types?',
        a: 'On the Customer Support Overview, the badge next to "Search Workbench" shows the total number of search types available. Click the Help button next to it to see a modal explaining each lookup and the identifiers it accepts.',
      },
    ],
  },
  '/ryd-customer-support/user-management': {
    urlParameters: ['?email={email}', '?userId={userId}', '?accountId={accountId}'],
    keywords: [
      'user management',
      'user lookup',
      'customer accounts',
      'account lookup',
      'user account',
      'block user',
      'unblock user',
      'reset password',
      'force logout',
      'manage user',
      'customer profile',
      'benutzerverwaltung',
      'user id',
      'account id',
      'userId',
      'accountId',
      'invoice email',
      'company name',
      'sponsor context',
      'identifiers and status',
      'profile panel',
      'user type',
      'share this search',
      'shareable link',
      'forwarded from name search',
      // Goodwill voucher is an action on this page rather than a route of its own,
      // so its vocabulary lives here — including the German "Kulanz", which is what
      // support actually calls it.
      'goodwill voucher',
      'goodwill',
      'kulanz',
      'kulanz gutschein',
      'issue goodwill voucher',
      'gift card',
      'geschenkkarte',
      'gutschein',
      'voucherify',
      'compensation',
      'campaigns and vouchers',
      'custom amount',
      'preset amount',
    ],
    questions: [
      'How do I block a customer account?',
      'How do I unblock a customer?',
      'How do I reset a customer password from support?',
      'How do I force-log-out a customer?',
      'How do I find a customer in the support backend?',
      'How do I copy a shareable link to a customer record?',
      'What is the difference between User ID and Account ID?',
      'How do I issue a goodwill voucher (Kulanz) to a customer?',
      'What is the maximum goodwill amount I can issue?',
      'Why can I only pick preset amounts for a goodwill voucher?',
      'Does the customer get notified about a goodwill voucher?',
      'Why is the goodwill voucher action disabled for this customer?',
    ],
    faqs: [
      {
        q: 'How do I block a fraudulent customer account?',
        a: 'Use Customer Support → User Management to look up the user, then use the block action on the account detail. Blocking takes effect immediately and prevents new orders.',
      },
      {
        q: 'How do I reset a customer password as customer support?',
        a: 'Open Customer Support → User Management, find the user, and use the Reset Password action on their account. The user receives an email with the reset link.',
      },
      {
        q: 'Can I look up a customer by either their User ID or Account ID?',
        a: 'Yes — the first input on User Management accepts both User ID and Account ID. Use Email in the second field as an alternative. The Share button copies a URL with the lookup pre-filled so a colleague opens the same record.',
      },
      {
        q: 'I came from the Name search and the user list is filtered — how do I clear it?',
        a: 'When User Management is opened from the Name search, the chosen first/last name appears as filter badges at the top. Click the Clear button on the badge row to drop the inherited filter.',
      },
      {
        q: 'How do I issue a goodwill voucher (Kulanz) to a customer?',
        a: 'Load the customer in Customer Support → User Management, then use Issue Goodwill Voucher from either the Actions card or the Campaigns & Vouchers card. Pick an amount, enter a reason of at least 5 characters including the ticket reference, then review and confirm. The backend creates, funds and assigns the gift card in one step.',
      },
      {
        q: 'What amounts can I issue as a goodwill voucher?',
        a: 'It depends on your role. customer_support_agent can pick from ten presets between €0.50 and €5.00. customer_support_admin can additionally enter a custom amount from €0.50 up to a €100.00 hard cap. EUR only, and the server re-validates the amount against your role.',
      },
      {
        q: 'Does the customer get an email when I issue a goodwill voucher?',
        a: 'No. There is no customer-facing notification of any kind — you reply on the existing support ticket. That is why the reason field is expected to contain the ticket reference.',
      },
      {
        q: 'Why can I not issue a goodwill voucher for this customer?',
        a: 'The customer must be Active and have an Account ID. Blocked customers, fully deleted or anonymised accounts, and PARTNER_BACKEND accounts are not eligible.',
      },
      {
        q: 'Can I issue more than one goodwill voucher to the same customer in a day?',
        a: 'Yes — the one-per-case-per-agent-per-day limit is a soft warning, not a block. You will be warned but not stopped, and the action is audited either way.',
      },
    ],
  },
  '/ryd-customer-support/find-data-by-order-uuid': {
    keywords: [
      'order uuid',
      'order id',
      'find order',
      'order lookup',
      'order search',
      'transaction id',
      'order trace',
      'order detail',
      'transaktions-id',
      'bestellnummer',
      'search by order uuid',
      'download pdf',
      'order pdf',
      'gdpr notice',
      'audit-logged',
      'audit logged',
      'law enforcement',
      'order customer',
      'redemption codes',
      'gift card details',
      'invoice details in odoo',
      'odoo sale orders',
      'invoicing creation steps',
      'queue messages',
      'invoicing traces',
      'user activity in ryd frontend',
      'client journey',
      'shareable url',
      'uuid query parameter',
    ],
    questions: [
      'How do I look up an order by UUID?',
      'Where do I paste an order ID to find a transaction?',
      'How do I see the status history of an order?',
      'A customer says order X failed — how do I check it?',
      'How do I download an order summary PDF?',
      'Where do I see Odoo invoice attachments for an order UUID?',
      'How do I share an order UUID search with a colleague?',
    ],
    faqs: [
      {
        q: 'A customer gave me an order UUID — how do I look it up?',
        a: 'Open Customer Support → Find by Order UUID and paste the UUID into the search field. The result shows the order, its action codes, and the related payment and station data. You can also open the page with `?uuid=<UUID>` to trigger the search automatically.',
      },
      {
        q: 'I have a transaction ID but no UUID — can I still find the order?',
        a: 'Not from the UUID lookup. Use Find by Email, Find by Voucher Code or Find by Invoice Number to narrow it down, or ask the customer for the order UUID from their receipt or app history.',
      },
      {
        q: 'When can I download the order PDF and what does it contain?',
        a: 'Once an order is loaded, the Download PDF button becomes available. The PDF includes personal data (name, email, IP address, location); every download is audit-logged with the operator and a timestamp, and is intended for support or law-enforcement use only.',
      },
      {
        q: 'Why is the page showing placeholder cards before I search?',
        a: 'Placeholder cards (Order details, Invoice details in Odoo, Invoicing creation steps, User activity, Order Customer) preview the panels you will see after running a search; the data only loads once you submit a UUID.',
      },
    ],
  },
  '/ryd-customer-support/find-data-by-voucher-code': {
    keywords: [
      'voucher',
      'voucher code',
      'promo code',
      'coupon',
      'discount code',
      'gutscheincode',
      'redeem code',
      'voucher status',
      'voucher redemption',
      'gift voucher',
      'GIFT_VOUCHER',
      'voucher type',
      'voucher campaign',
      'voucher details',
      'gift card buyer',
      'consumer redeemer',
      'redemptions used',
      'redemption quantity',
      'unlimited redemptions',
      'enable voucher',
      'disable voucher',
      'active voucher',
      'inactive voucher',
      'code query parameter',
    ],
    questions: [
      'How do I check whether a voucher code has been redeemed?',
      'Where do I look up a promo code?',
      'How do I see who used a particular voucher?',
      'Why is a voucher code being rejected?',
      'How do I disable or re-enable a voucher?',
      'How many redemptions does a voucher have left?',
    ],
    faqs: [
      {
        q: 'How do I check whether a voucher code has been redeemed?',
        a: 'Open Customer Support → Find by Voucher Code and enter the code. Voucher Details show status, type, campaign and the redemption count vs. the configured maximum (`unlimited` if no cap).',
      },
      {
        q: 'How do I disable or re-enable a voucher from support?',
        a: 'On the Voucher Details panel of Find by Voucher Code, the role-gated Available Actions area exposes a Disable Voucher button when the voucher is active and an Enable Voucher button when it is inactive. The change takes effect immediately.',
      },
      {
        q: 'A customer bought a gift voucher — how do I find the buyer?',
        a: 'For voucher type `GIFT_VOUCHER`, the Gift Card Buyer panel on the Find by Voucher Code page shows the buyer details and the original purchase order; the Consumer/Redeemer Redemptions panel below lists each redeemer separately.',
      },
    ],
  },
  '/ryd-customer-support/find-data-by-email-partial': {
    keywords: [
      'email lookup',
      'email search',
      'partial email',
      'find user by email',
      'bulk actions',
      'partial search',
      'email contains',
      'email-suche',
      'partial email match',
      'bulk operations',
      'block selected',
      'unblock selected',
      'idempotent block',
      'orders from utc',
      'orders to utc',
      'date range',
      'found users',
      'capped at 100 users',
      'capped at 2500 orders',
      'orders truncated',
      'orders capped',
      'select all',
      'deselect all',
    ],
    questions: [
      'How do I find a customer by partial email?',
      'I only know part of the customer email — what now?',
      'How do I run a bulk action across multiple matched users?',
      'How do I block or unblock several users at once?',
      'Why is the order list capped at 2,500 results?',
    ],
    faqs: [
      {
        q: 'I only know part of a customer email. How do I find them?',
        a: 'Open Customer Support → Partial email search & bulk actions and enter the partial string. The lookup returns up to 100 matching customer accounts so you can pick the right one or run a bulk action.',
      },
      {
        q: 'How do I block several customer accounts at once?',
        a: 'On the Partial email search page, select the checkboxes next to each user you want to act on, then use the Block selected button. Block/Unblock is idempotent — running it on an already blocked or unblocked user is safe.',
      },
      {
        q: 'Why does the orders table say it was capped at 2,500?',
        a: 'The page fetches orders for all matched users in the chosen date range and caps the combined result at 2,500 to keep the UI responsive. Shorten the date range to surface the most recent orders within the limit.',
      },
    ],
  },
  '/ryd-customer-support/find-data-by-payment-email': {
    keywords: [
      'payment email',
      'paypal email',
      'find by payment email',
      'payment account',
      'paypal lookup',
      'payment owner',
      'payment method email',
      'payment method groups',
      'unique customers',
      'customerId',
      'payment instrument email',
      'rfp paymentMethods byEmail',
    ],
    questions: [
      'How do I find a customer by their PayPal / payment email?',
      'Where do I look up an order by the payment email used?',
      'Which customers share the same payment email?',
    ],
    faqs: [
      {
        q: 'How do I find a customer using their PayPal payment email?',
        a: 'Open Customer Support → Find by Payment Email and enter the email associated with the payment instrument. The page lists Payment Method Groups for that email and the Unique Customers section resolves each customerId to a user.',
      },
      {
        q: 'Why are several customers shown for one payment email?',
        a: 'A single payment email (e.g. a shared PayPal account) can be linked to multiple customer accounts. The Unique Customers panel deduplicates the customer IDs across the matching payment methods so you can see all of them at once.',
      },
    ],
  },
  '/ryd-customer-support/find-data-by-name': {
    keywords: [
      'find by name',
      'name search',
      'customer name lookup',
      'first name',
      'last name',
      'name suche',
      'partial first name',
      'partial last name',
      'substring match',
      'sortable columns',
      'filter visible columns',
      'forwards to user management',
      'single hit forwards',
    ],
    questions: [
      'How do I find a customer by their full name?',
      'Where do I search by first or last name?',
      'Can I search by just first name or just last name?',
      'How do I sort the name-search results?',
    ],
    faqs: [
      {
        q: 'Can I search by just a partial first or last name?',
        a: 'Yes — Find by Name accepts a partial First Name and/or partial Last Name and matches both as substrings. You only need to fill one of the two fields.',
      },
      {
        q: 'What happens if my name search matches a single user?',
        a: 'A single hit is forwarded directly to User Management with the chosen name pre-applied as a filter. Multiple hits are listed in a sortable, filterable table on the Name search page so you can pick the right one.',
      },
    ],
  },
  '/ryd-customer-support/find-data-by-invoice-number': {
    keywords: [
      'invoice number',
      'invoice lookup',
      'find invoice',
      'rechnungsnummer',
      'invoice search',
      'odoo invoice',
      'find by invoice',
      'invoice id',
      'b2c invoices',
      'fleet invoices',
      'credit notes',
      'salesforce invoice numbers',
      'INV/2025',
      'partial invoice match',
      'min 4 digits',
      'sale order count',
      'sale orders',
      'invoice amount',
      'invoice currency',
      'invoice state',
    ],
    questions: [
      'How do I find an order by invoice number?',
      'Where do I look up an Odoo invoice from support?',
      'Can I search for invoices with a partial number?',
      'Does the invoice search cover credit notes and Salesforce invoices?',
    ],
    faqs: [
      {
        q: 'A customer gave me an invoice number — how do I find the related order?',
        a: 'Open Customer Support → Find by Invoice Number and paste the number. The page links the invoice to the underlying sale orders, the customer and the payment.',
      },
      {
        q: 'Which invoice types does the invoice-number search support?',
        a: 'It supports B2C invoices, fleet invoices, credit notes and Salesforce invoice numbers. Partial matches are allowed — for example, entering `1910177` finds `INV/2025/1910177` — but the input must be at least 4 digits long.',
      },
      {
        q: 'I get many invoices back — how do I narrow the result down?',
        a: 'When the search returns more than one hit, the page shows the first results in a table and prompts you to refine the search. Add more digits, or include the year prefix (e.g. `INV/2025/`), to surface a single invoice and load its detail view.',
      },
    ],
  },
  '/ryd-customer-support/remote-fueling': {
    keywords: [
      'remote fueling',
      'remote refuelling',
      'remote tankvorgang',
      'fraud',
      'gps mock',
      'suspicious refuel',
      'remote pump',
      'remote authorization',
      'remote fueling detection',
      'review status',
      'bulk update review status',
      'audit log comment',
      'manual rescan',
      'trigger scan now',
      'rescanned twice daily',
      '00:00 UTC',
      '12:00 UTC',
      'firebase analytics',
      'bigquery',
      'station coordinates',
      'export to csv',
      'remote fueling list',
      'fraud detection',
    ],
    questions: [
      'How do I investigate a suspected remote fueling case?',
      'Where do I see remote-fueling alerts?',
      'What is remote fueling and why is it tracked?',
      'How do I update the review status for many remote-fueling records at once?',
      'How fresh is the remote fueling data?',
      'How do I trigger a manual remote-fueling rescan?',
    ],
    faqs: [
      {
        q: 'What is remote fueling and how do I look into a case?',
        a: 'Remote fueling describes orders where the customer was not physically at the pump — typically a fraud signal. Open Customer Support → Remote Fueling to see flagged orders, the GPS evidence, and the linked customer.',
      },
      {
        q: 'How fresh is the remote-fueling data?',
        a: 'It is automatically rescanned twice daily at 00:00 and 12:00 UTC, because the result is derived from multiple sources (Firebase Analytics, BigQuery, station coordinates). Use Trigger Scan Now in the Data Freshness card if you need the latest data immediately.',
      },
      {
        q: 'How do I bulk-update the review status of remote-fueling records?',
        a: 'Select records in the result table, click Bulk Update Review Status, pick the new status and apply. An audit-log comment is created for each affected record so the change is traceable.',
      },
    ],
  },
  '/ryd-customer-support/suspicious-devices': {
    keywords: [
      'suspicious devices',
      'multiple accounts',
      'fraud detection',
      'hacker analysis',
      'device fingerprint',
      'shared device',
      'fraud devices',
      'suspicious device list',
      'device key',
      'review status',
      'bulk update review status',
      'audit log comment',
      'manual rescan',
      'trigger scan now',
      'rescanned twice daily',
      '00:00 UTC',
      '12:00 UTC',
      'firebase analytics events',
      'app tables',
      'export to csv',
    ],
    questions: [
      'How do I find suspicious devices linked to multiple customer accounts?',
      'Where do I see the fraud-flagged devices?',
      'How do I bulk-update the review status for suspicious devices?',
      'How fresh is the suspicious-device data?',
    ],
    faqs: [
      {
        q: 'What does the Suspicious Devices page show?',
        a: 'It lists devices that have been associated with multiple customer accounts and other fraud signals. Use it to spot account-sharing, automated abuse and credential-reuse patterns.',
      },
      {
        q: 'How fresh is the suspicious-device list?',
        a: 'Suspicious-device data is automatically rescanned twice daily at 00:00 and 12:00 UTC, because the result is derived from Firebase Analytics events across multiple app tables. Trigger Scan Now in the Data Freshness card forces a rescan.',
      },
      {
        q: 'How do I update the review status of several flagged devices at once?',
        a: 'Select the rows you want to act on, open Bulk Update Review Status, pick the new status and apply. An audit-log comment is created per device so the change is auditable.',
      },
    ],
  },
  '/ryd-customer-support/gdpr-export': {
    urlParameters: ['?email={email}'],
    keywords: [
      'gdpr',
      'dsgvo',
      'DSGVO',
      'gdpr export',
      'dsgvo export',
      'dsgvo-export',
      'dsgvo/gdpr export',
      'dsgvo / gdpr',
      'data subject access request',
      'sar',
      'art 15',
      'art. 15',
      'art. 15 dsgvo',
      'art 15 dsgvo',
      'art. 15 gdpr',
      'article 15 gdpr',
      'article 15',
      'right of access',
      'customer data export',
      'personal data export',
      'compliance',
      'datenschutz',
      'datenschutzanfrage',
      'datenschutzauskunft',
      'datenauskunft',
      'datenexport',
      'datenschutz-grundverordnung',
      'datenschutzgrundverordnung',
      'eu-datenschutz',
      'auskunft',
      'auskunftsersuchen',
      'auskunftsanspruch',
      'auskunft nach art. 15 dsgvo',
      'kundenanfrage datenschutz',
      'subject access',
      'export by email',
      'gdpr_admin',
      'gdpr admin',
      'internal only',
      'ryd internal',
      'zip',
      'pdf',
      'account history',
      'payment methods',
      'consents',
      'marketing',
      'mailchimp',
      'mask payment',
      'redact',
      'audit log',
    ],
    questions: [
      'How do I generate a GDPR / DSGVO data export for a customer?',
      'How do I trigger a DSGVO data export from customer support?',
      'Which role do I need to use the GDPR Export page?',
      'What does the GDPR export ZIP contain?',
      'In which language is the GDPR PDF generated?',
      'Where are the generated GDPR export files stored?',
      'How long are GDPR export files kept?',
      'Where can I see who triggered or downloaded a GDPR export?',
      'Why does my GDPR export show no account history?',
      'Can I email a GDPR export directly to the customer?',
    ],
    faqs: [
      {
        q: 'How do I trigger a GDPR / DSGVO data export from the portal?',
        a: 'Open Customer Support → GDPR Export, enter the customer email, click Search to see prior jobs, then click "Trigger new export" and add a short reason (e.g. ticket id). The job runs asynchronously; when the status reaches COMPLETED you can download the ZIP. This page is internal only and requires the gdpr_admin role.',
      },
      {
        q: 'Which role do I need to use the GDPR Export page?',
        a: 'The dedicated gdpr_admin role. Holding customer_support_admin or customer_support_agent is not enough — gdpr_admin has to be granted explicitly. The page is also gated by userGroup=ryd, so external partners cannot reach it.',
      },
      {
        q: 'What is in the GDPR export ZIP?',
        a: 'A German (or English) PDF cover with the DSGVO Art. 13/14 narrative, plus JSON files: user profile, vehicles, account, account history (up to 200 entries), masked payment methods (no PAN/CVV, PayPal token redacted), consents and marketing (tags + Mailchimp), and a machine-readable manifest with per-section provenance.',
      },
      {
        q: 'In which language is the GDPR PDF generated?',
        a: 'The PDF is rendered in the customer\'s preferred language: English when the user-service localisation.language starts with "en", German otherwise (the default). Both layouts cover controller, purpose, data categories, recipients, retention, data sources and rights.',
      },
      {
        q: 'How long are GDPR export files retained?',
        a: 'There is no automatic expiration today (product decision). The ZIP stays in the private GCS bucket under gdpr-exports/<sha256(email)>/<yyyy>/<mm>/<jobId>_… until removed manually. Download URLs themselves expire after 15 minutes, but the underlying file does not.',
      },
      {
        q: 'Why does the manifest show no account-history records?',
        a: 'The account-history-service may not be routed on the current API gateway host. Open rp-users/src/__tests__/ryd-api-gateway-clients/account-history-client.tester.ts to probe the gateway with and without the env suffix. The manifest errors[] field will also show the exact upstream error.',
      },
      {
        q: 'Can the customer support team email the GDPR export directly from the portal?',
        a: 'Not today. The agent downloads the ZIP from the GDPR Export page and forwards it manually via the support channel. Direct email-sending from the page can be added later if the team needs it.',
      },
      {
        q: 'Where is the audit trail of GDPR triggers and downloads?',
        a: 'Every action writes a customer-support BigQuery audit event: CUSTOMER_SUPPORT_GDPR_EXPORT_TRIGGERED, _DOWNLOADED, _FAILED, with subjectEmail, jobId, triggerReason, storagePath and actor. The GDPR Export page also shows a mini audit table directly under the jobs table for the searched email.',
      },
    ],
  },
  '/ryd-customer-support/account-deletions': {
    urlParameters: ['?email={email}', '?accountId={accountId}', '?userId={userId}'],
    keywords: [
      'account deletion',
      'account deletions',
      'deleted account',
      'deletion record',
      'deletion date',
      'deletion channel',
      'when was the account deleted',
      'how was the account deleted',
      'who deleted the account',
      'deleted in the app',
      'deleted by support',
      'backoffice deletion',
      'fleet portal deletion',
      'guest clean-up',
      'löschung',
      'kontolöschung',
      'account gelöscht',
      'löschdatum',
      'löschweg',
      'löschnachweis',
      'nachweis der löschung',
      'wann gelöscht',
      'wie gelöscht',
      'dsgvo',
      'gdpr',
      'gdpr_admin',
      'anonymised email',
      'thinxnet.com',
      'former email',
      'search by deleted email',
      'sync now',
      'reconciliation',
      'audit log',
      'internal only',
    ],
    questions: [
      'How do I find out when an account was deleted?',
      'How do I find out whether a customer deleted the account in the app or Support did it?',
      'How can I find a deleted customer by their old e-mail address?',
      'Which role do I need for the Account Deletions page?',
      'Why is a deletion not found on the Account Deletions page?',
      'What does "inferred" mean on a deletion record?',
      'Is viewing a deletion record logged?',
    ],
    faqs: [
      {
        q: 'How do I find out when and how an account was deleted?',
        a: 'Open Customer Support → Account Deletions, enter the customer’s former e-mail (exact), the account id or the user id and click Search. The record shows the deletion date, the channel (in the app, Customer Support portal, Backoffice, fleet portal or a system clean-up), who performed it and the evidence. Customer Support roles also see the record (without the e-mail) on the customer overview in User Management.',
      },
      {
        q: 'Which role do I need for the Account Deletions page?',
        a: 'The gdpr_admin role, like the GDPR Export page, plus the ryd user group. Customer Support roles can see the record on the customer overview but not search by e-mail.',
      },
      {
        q: 'Why is a deletion not found on the Account Deletions page?',
        a: 'The page searches stored records only. Records are created by the nightly sync (03:15 UTC, last 50 hours) and by opening a deleted customer in User Management. Click Sync Now to pick up recent deletions, or open the customer by account id in User Management to derive the record on the spot. For deletions older than the backend log retention the e-mail may not be recoverable; search by account id instead.',
      },
      {
        q: 'What does "inferred" mean on a deletion record?',
        a: 'No direct proof of the channel survived. The channel was derived from indirect traces, for example an archive job that carried a request correlation id (only the app passes one), a gateway request line without a portal audit row (Backoffice), or a Delete Account screen view in the app analytics close to the deletion time.',
      },
      {
        q: 'Is viewing a deletion record logged?',
        a: 'Yes. Every record opened on the Account Deletions page writes CUSTOMER_SUPPORT_ACCOUNT_DELETION_RECORD_SEARCH_VIEWED with your user, the search key and the account id; reads on the customer overview write CUSTOMER_SUPPORT_ACCOUNT_DELETION_RECORD_VIEWED. The records themselves are ACCOUNT_DELETION_RECORDED rows in the business audit log.',
      },
    ],
  },
};
